<script lang="ts">
  import { ExternalLink, Trash2, Sparkles } from "lucide-svelte";
  import type { WalletCard, WalletBenefit } from "../wallet/types";
  import { summarizeBuckets, type BucketSummary, BUCKETS } from "./bucketize";
  import { categoryLabel } from "../wallet/data";
  import { confidenceTier, confidenceLabel, confidenceBadgeVariant } from "./bucketize";
  import { formatCnyApprox } from "./lib/format";
  import Sheet from "./ui/Sheet.svelte";
  import Badge from "./ui/Badge.svelte";
  import Button from "./ui/Button.svelte";
  import CardThumb from "./CardThumb.svelte";
  import type { Profile } from "./profile";

  export let card: WalletCard | null;
  export let allBenefits: WalletBenefit[];
  export let profile: Profile;
  export let onClose: () => void;
  export let onRemove: () => void;

  $: open = !!card;
  $: cardBenefits = card ? allBenefits.filter(b => b.cardId === card.id) : [];

  type Group = { bucketId: string; bucketTitle: string; bucketEmoji: string; benefits: WalletBenefit[] };

  $: groups = (() => {
    if (!card) return [] as Group[];
    const fakeProfile: Profile = { ...profile, scenarios: profile.scenarios.length ? profile.scenarios : ["online_shop", "dining"] };
    const summary: BucketSummary[] = summarizeBuckets(cardBenefits, fakeProfile);
    return summary
      .filter(s => s.def.id !== "daily" && (s.benefits.length > 0 || s.pendingReview.length > 0))
      .map(s => ({
        bucketId: s.def.id,
        bucketTitle: s.def.title,
        bucketEmoji: s.def.emoji,
        benefits: [...s.benefits, ...s.pendingReview]
      }));
  })();

  $: trustedCount = cardBenefits.filter(b => {
    const t = confidenceTier(b);
    return t === "high" || t === "medium";
  }).length;

  $: totalValue = cardBenefits.reduce((sum, b) => sum + (b.estimateValueCny || 0), 0);

  $: isCustom =
    card?.sourceLevel === "user_self_reported" || card?.coverageStatus === "user_self_reported";
  $: isPending = !isCustom && (card?.sourceLevel === "official_pending" || card?.coverageStatus === "auto_discovered");
</script>

<Sheet
  open={open}
  title={card ? card.shortName || card.name : ""}
  onClose={onClose}
>
  {#if card}
    <div class="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
      <CardThumb {card} size="lg" tilt />
      <div class="min-w-0 flex-1 text-center sm:text-left">
        <p class="text-xs text-muted-foreground">{card.issuer} · {card.level}</p>
        <p class="mt-2 text-xs text-muted-foreground">年费</p>
        <p class="text-sm font-medium leading-snug text-foreground">{card.annualFeeText}</p>
      </div>
    </div>

    <div class="mt-6 grid grid-cols-3 gap-3">
      <div class="rounded-xl border bg-card/60 px-3 py-3 text-center">
        <p class="font-label text-muted-foreground">权益总数</p>
        <p class="mt-1 font-display text-2xl text-foreground">{cardBenefits.length}</p>
      </div>
      <div class="rounded-xl border bg-card/60 px-3 py-3 text-center">
        <p class="font-label text-muted-foreground">已核实</p>
        <p class="mt-1 font-display text-2xl text-success">{trustedCount}</p>
      </div>
      <div class="rounded-xl border bg-card/60 px-3 py-3 text-center">
        <p class="font-label text-muted-foreground">估算总值</p>
        <p class="mt-1 font-display text-xl text-primary">
          {formatCnyApprox(totalValue)}
        </p>
      </div>
    </div>

    {#if isCustom}
      <div class="mt-5 rounded-xl border border-warn/30 bg-warn/10 px-4 py-3 text-xs">
        <p class="flex items-center gap-1.5 font-semibold text-warn">
          <Sparkles class="size-3.5" />
          这是你自录的卡
        </p>
        <p class="mt-1 text-muted-foreground">
          我们还没正式收录这张卡的权益规则。「我有这张」信号已提交收录队列。
        </p>
      </div>
    {:else if isPending}
      <div class="mt-5 rounded-xl border border-warn/30 bg-warn/10 px-4 py-3 text-xs">
        <p class="flex items-center gap-1.5 font-semibold text-warn">
          <Sparkles class="size-3.5" />
          扩充收录卡 · 信息以官方为准
        </p>
        <p class="mt-1 text-muted-foreground">
          年费、刷免、积分抵扣等关键数字以发卡行 App 或账单核对为准。
          权益规则我们还在按官方页面逐项录入；用过的话请用底部「用过/规则不对」打个标，帮我们排优先级。
        </p>
      </div>
    {/if}

    {#if groups.length > 0}
      <div class="mt-6 space-y-4">
        {#each groups as g}
          <section>
            <div class="mb-2 flex items-center gap-2">
              <span class="text-base">{g.bucketEmoji}</span>
              <span class="font-label text-muted-foreground">{g.bucketTitle} · {g.benefits.length} 条</span>
            </div>
            <ul class="space-y-2">
              {#each g.benefits as b}
                {@const tier = confidenceTier(b)}
                <li class="rounded-xl border bg-secondary/40 px-3 py-3">
                  <div class="flex flex-wrap items-start justify-between gap-2">
                    <p class="text-sm font-medium text-foreground">{b.title}</p>
                    <Badge variant={confidenceBadgeVariant(tier)} class="text-[10px] shrink-0">
                      {confidenceLabel(tier)}
                    </Badge>
                  </div>
                  <div class="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Badge variant="outline" class="text-[10px]">{categoryLabel(b.category)}</Badge>
                    {#if b.estimateValueCny > 0}
                      <span>·</span>
                      <span>{formatCnyApprox(b.estimateValueCny)}</span>
                    {/if}
                    {#if b.quotaText}
                      <span>·</span>
                      <span>{b.quotaText}</span>
                    {/if}
                  </div>
                  <p class="mt-1.5 text-xs">{b.usageChannel}</p>
                  {#if b.sourceUrl}
                    <a
                      href={b.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="focus-amber mt-2 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                    >
                      <ExternalLink class="size-3" />
                      {b.sourceName || "官方来源"}
                    </a>
                  {/if}
                </li>
              {/each}
            </ul>
          </section>
        {/each}
      </div>
    {:else}
      <p class="mt-6 rounded-xl border border-dashed bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground">
        {isCustom ? "自录卡暂无录入权益。" : "暂无与你画像匹配的权益规则。"}
      </p>
    {/if}

    {#if card.sourceUrls?.length}
      <div class="mt-5 border-t border-dashed border-foreground/10 pt-4">
        <p class="font-label mb-2 text-muted-foreground">官方来源</p>
        <ul class="space-y-1.5">
          {#each card.sourceUrls.slice(0, 3) as url}
            <li>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                class="focus-amber inline-flex items-center gap-1 break-all text-[11px] text-primary hover:underline"
              >
                <ExternalLink class="size-3 shrink-0" />
                <span class="truncate">{url}</span>
              </a>
            </li>
          {/each}
        </ul>
      </div>
    {/if}

    <div class="mt-6 flex items-center justify-end gap-2 border-t border-dashed border-foreground/10 pt-4">
      <Button variant="ghost" size="sm" on:click={onClose}>关闭</Button>
      <Button
        variant="outline"
        size="sm"
        class="text-destructive hover:border-destructive/40 hover:text-destructive"
        on:click={onRemove}
      >
        <Trash2 class="mr-1 size-3.5" />
        从卡包移除
      </Button>
    </div>
  {/if}
</Sheet>
